import { useCallback } from 'react';
import { useWindowDimensions } from 'react-native';
import { Flex, RemoteImage } from '@banhala/ui-kit';
import type { RelatedEventProps, RelatedEventItem } from './types';
import type { AnyObject } from '../../../../types';
import { bridge } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';

const SINGLE_HORIZONTAL_PADDING = 16;
const RELATED_HORIZONTAL_PADDING = 12;
const RELATED_GAP = 12;
const GRID_GAP = 6;
const SINGLE_ASPECT_RATIO = 343 / 200;

export const RelatedEvent = ({
  entity,
  body,
  logging,
}: RelatedEventProps) => {
  const { width: screenWidth } = useWindowDimensions();
  const { openDeeplink } = useOpenDeeplink();

  const handlePress = useCallback(
    (item: RelatedEventItem, itemLogging: AnyObject) => {
      if (!item.deeplink) return;
      // iOS: 컴포넌트 + 아이템 로깅 머지 패턴
      bridge.logAnalyticsEvent({
        name: 'CLICK_RELATED_EVENT',
        analytics: {
          ...logging.analytics,
          ...itemLogging.analytics,
        },
        ably_only_log: {
          ...(logging.ably_only_log as AnyObject),
          ...(itemLogging.ably_only_log as AnyObject),
        },
      });
      openDeeplink(item.deeplink);
    },
    [openDeeplink, logging],
  );

  if (!entity || entity.length === 0) return null;

  const firstEntity = entity[0];
  const item = firstEntity.item;
  const itemLogging = firstEntity.logging;
  const layoutType = body.item.type;

  // SINGLE 레이아웃: 전폭 배너 이미지
  if (layoutType === 'SINGLE') {
    const imageWidth = screenWidth - SINGLE_HORIZONTAL_PADDING * 2;
    const imageHeight = imageWidth / SINGLE_ASPECT_RATIO;

    return (
      <Flex padding={{ horizontal: SINGLE_HORIZONTAL_PADDING }}>
        <Flex borderRadius={6} overflow="hidden" onPress={() => handlePress(item, itemLogging)}>
          <RemoteImage
            uri={item.thumbnail}
            width={imageWidth}
            height={imageHeight}
          />
        </Flex>
      </Flex>
    );
  }

  // LEFT_RELATED / RIGHT_RELATED 레이아웃
  const contentWidth =
    screenWidth - RELATED_HORIZONTAL_PADDING * 2 - RELATED_GAP;
  const halfWidth = contentWidth / 2;
  // 배너 이미지는 정사각형 영역에 맞춤
  const bannerSize = halfWidth;
  // 2×2 그리드: 각 셀 크기
  const gridCellSize = (halfWidth - GRID_GAP) / 2;

  const thumbnails = item.goods_thumbnails.slice(0, 4);

  const bannerImage = (
    <Flex borderRadius={4} overflow="hidden" onPress={() => handlePress(item, itemLogging)}>
      <RemoteImage
        uri={item.thumbnail}
        width={halfWidth}
        height={bannerSize}
      />
    </Flex>
  );

  const gridView = (
    <Flex
      flex={1}
      onPress={() => handlePress(item, itemLogging)}
    >
      <Flex.Row flexWrap="wrap" width={halfWidth} height={bannerSize}>
        {thumbnails.map((thumbUrl, index) => (
          <Flex
            key={index}
            borderRadius={4}
            overflow="hidden"
            margin={{
              right: index % 2 === 0 ? GRID_GAP : 0,
              bottom: index < 2 ? GRID_GAP : 0,
            }}
          >
            <RemoteImage
              uri={thumbUrl}
              width={gridCellSize}
              height={gridCellSize}
            />
          </Flex>
        ))}
      </Flex.Row>
    </Flex>
  );

  return (
    <Flex padding={{ horizontal: RELATED_HORIZONTAL_PADDING }}>
      <Flex.Row gap={RELATED_GAP}>
        {layoutType === 'LEFT_RELATED' ? (
          <>
            {bannerImage}
            {gridView}
          </>
        ) : (
          <>
            {gridView}
            {bannerImage}
          </>
        )}
      </Flex.Row>
    </Flex>
  );
};
